import React from 'react';

export interface HJSliderProps {
  /** Inline label shown to the left of the value readout. */
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (v: number) => void;
  /** Override the small numeric readout — e.g. "+24" with sign, or a custom unit. */
  formatValue?: (v: number) => string;
}

/** Design-system slider for the enhancer's color-grade controls.
 *  Distinct from shadcn's Slider — uses the brand's blue accent, a center
 *  midpoint, and bipolar fill (-100..+100 with the bar filling outward from
 *  the center). Behind the visuals is a native range input for accessibility
 *  and keyboard support. */
export const HJSlider: React.FC<HJSliderProps> = ({
  label,
  value,
  min = -100,
  max = 100,
  step = 1,
  onChange,
  formatValue,
}) => {
  const pct = ((value - min) / (max - min)) * 100;
  const sign = value > 0 ? '+' : '';
  const display = formatValue ? formatValue(value) : `${sign}${value}`;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(Number(e.target.value));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-[13px]">
        <span style={{ color: 'var(--hj-ink)' }}>{label}</span>
        <span
          className="tabular font-hj-mono"
          style={{ color: 'var(--hj-ink)' }}
        >
          {display}
        </span>
      </div>
      <div
        className="relative flex items-center min-h-[44px] sm:min-h-0"
        style={{ height: undefined }}
      >
        {/* Track */}
        <div
          className="absolute left-0 right-0 rounded-full"
          style={{ height: 3, background: 'var(--hj-surface-3)' }}
        />
        {/* Bipolar fill — extends from center outward in the direction of value */}
        <div
          className="absolute rounded-full"
          style={{
            left: '50%',
            width: `${Math.abs(pct - 50)}%`,
            height: 3,
            background: 'var(--hj-blue)',
            transform: value < 0 ? 'translateX(-100%)' : 'translateX(0)',
          }}
        />
        {/* Handle */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${pct}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 14,
            height: 14,
            background: 'var(--hj-ink)',
            border: '2px solid var(--hj-blue)',
          }}
        />
        {/* Native input on top, transparent, captures input + keyboard */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          aria-label={label}
        />
      </div>
    </div>
  );
};
